import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import io from 'socket.io-client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Radio, Video, Users, Loader2 } from 'lucide-react';
import {
  createPeerConnection,
  handleOffer,
  createAnswer,
  addIceCandidate
} from '@/utils/webrtc';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveStreamView = () => {
  const { t } = useTranslation();
  const [stream, setStream] = useState(null);
  const [showStreamDialog, setShowStreamDialog] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [remoteStream, setRemoteStream] = useState(null);
  
  // Refs
  const socketRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const userRef = useRef(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      userRef.current = JSON.parse(userStr);
    }
    
    fetchStream();
    
    // Poll for stream updates every 10 seconds
    const interval = setInterval(fetchStream, 10000);
    
    return () => {
      clearInterval(interval);
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (remoteStream && remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  const cleanup = () => {
    console.log('🧹 Student cleanup...');
    
    if (peerConnectionRef.current) {
      console.log('🔌 Closing peer connection');
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    if (socketRef.current) {
      console.log('🔌 Disconnecting socket');
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    setRemoteStream(null);
    setIsConnected(false);
    setIsConnecting(false);
    
    console.log('✅ Student cleanup complete');
  };

  const fetchStream = async () => {
    try {
      const response = await axios.get(`${API}/student/live-stream`);
      const fetchedStream = response.data.stream;
      
      // If stream was active but now it's not, cleanup
      if (stream && !fetchedStream && isConnected) {
        cleanup();
        toast.info(t('streamEnded') || 'انتهى البث المباشر');
      }
      
      setStream(fetchedStream);
    } catch (error) {
      console.error('Error fetching stream:', error);
    }
  };

  const initializeSocket = (roomName) => {
    return new Promise((resolve, reject) => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }

      socketRef.current = io(BACKEND_URL, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      socketRef.current.on('connect', () => {
        console.log('Socket connected:', socketRef.current.id);
        
        // Join the room
        socketRef.current.emit('join_room', {
          room_name: roomName,
          user_id: userRef.current.id,
          user_name: userRef.current.name,
          user_role: 'student'
        });
      });

      socketRef.current.on('room-joined', async (data) => {
        console.log('Joined room:', data);
        resolve(data);
      });

      socketRef.current.on('user-joined', (data) => {
        console.log('Another user joined:', data);
      });

      socketRef.current.on('user-left', (data) => {
        console.log('User left:', data);
        
        // If it's the teacher who left, cleanup
        if (data.user_role === 'teacher') {
          cleanup();
          toast.info(t('teacherLeftStream') || 'غادر المعلم البث');
        }
      });

      socketRef.current.on('offer', async (data) => {
        console.log('Received offer from:', data.sender_sid);
        await handleIncomingOffer(data.sender_sid, data.offer, data.sender_id);
      });

      socketRef.current.on('answer', async (data) => {
        console.log('Received answer from:', data.sender_sid);
        // Students typically don't receive answers, only teachers do
      });

      socketRef.current.on('ice_candidate', async (data) => {
        if (peerConnectionRef.current && data.candidate) {
          await addIceCandidate(peerConnectionRef.current, data.candidate);
        }
      });

      socketRef.current.on('error', (data) => {
        console.error('Socket error:', data);
        toast.error(data.message || t('error'));
        reject(new Error(data.message));
      });

      socketRef.current.on('disconnect', () => {
        console.log('Socket disconnected');
        setIsConnected(false);
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, 10000);
    });
  };

  const handleIncomingOffer = async (senderSid, offer, senderId) => {
    try {
      console.log('📥 Handling incoming offer from:', senderSid);
      
      if (!peerConnectionRef.current) {
        console.log('🔧 Creating new peer connection...');
        // Create peer connection
        peerConnectionRef.current = createPeerConnection(
          (candidate) => {
            // Send ICE candidate
            console.log('📤 Sending ICE candidate to teacher');
            socketRef.current.emit('ice_candidate', {
              target_sid: senderSid,
              candidate: candidate,
              sender_id: userRef.current.id
            });
          },
          (stream) => {
            console.log('✅ Received remote stream from teacher!');
            console.log('Stream tracks:', stream.getTracks().length);
            stream.getTracks().forEach(track => {
              console.log(`  - ${track.kind}: ${track.label} (${track.readyState})`);
            });
            setRemoteStream(stream);
            setIsConnected(true);
            setIsConnecting(false);
            toast.success(t('connectedToStream') || 'متصل بالبث المباشر');
          }
        );
      }

      // Handle the offer
      console.log('🔄 Setting remote description...');
      await handleOffer(peerConnectionRef.current, offer);
      
      // Create and send answer
      console.log('📤 Creating and sending answer...');
      const answer = await createAnswer(peerConnectionRef.current);
      
      socketRef.current.emit('answer', {
        target_sid: senderSid,
        answer: answer,
        sender_id: userRef.current.id
      });

      console.log('✅ Answer sent to teacher:', senderSid);
    } catch (error) {
      console.error('❌ Error handling offer:', error);
      setIsConnecting(false);
      toast.error(t('connectionError') || 'خطأ في الاتصال');
    }
  };

  const joinStream = async () => {
    if (!stream) return;

    setIsConnecting(true);
    setShowStreamDialog(true);

    try {
      // Initialize socket and join room
      await initializeSocket(stream.room_name);
      
      toast.success(t('connectedToStream') || 'متصل بالبث المباشر');
    } catch (error) {
      console.error('Error joining stream:', error);
      setIsConnecting(false);
      toast.error(t('errorJoiningStream') || 'خطأ في الانضمام للبث');
    }
  };

  const leaveStream = () => {
    if (socketRef.current && stream) {
      socketRef.current.emit('leave_room', {
        room_name: stream.room_name,
        user_id: userRef.current.id
      });
    }
    
    cleanup();
    setShowStreamDialog(false);
    toast.info(t('leftStream') || 'غادرت البث المباشر');
  };

  if (!stream) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Radio className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {t('noActiveStream') || 'لا يوجد بث مباشر متاح'}
            </h3>
            <p className="text-gray-500">
              {t('noStreamMessage') || 'لا يوجد بث مباشر نشط حالياً. سيتم إعلامك عند بدء المدرب بث جديد.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Radio className="h-8 w-8 text-red-500 animate-pulse" />
          {t('liveStream') || 'البث المباشر'}
        </h2>
      </div>

      {/* Live Stream Card */}
      <Card className="border-red-500 border-2 bg-gradient-to-br from-red-50 to-pink-50">
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-3 w-3 bg-red-600 rounded-full animate-pulse" />
                  <span className="text-sm font-semibold text-red-700">
                    {t('liveNow') || 'مباشر الآن'}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {stream.title}
                </h3>
                {stream.description && (
                  <p className="text-gray-600 mb-4">{stream.description}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {stream.teacher_name}
                  </span>
                  <span>
                    {t('startedAt') || 'بدأ في'}: {new Date(stream.started_at).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>

            <Button
              onClick={joinStream}
              className="w-full bg-gradient-to-r from-red-600 to-pink-600 text-lg py-6"
              disabled={isConnecting || isConnected}
            >
              {isConnecting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {t('connecting') || 'جاري الاتصال...'}
                </>
              ) : isConnected ? (
                <>
                  <Video className="h-5 w-5 mr-2" />
                  {t('connected') || 'متصل'}
                </>
              ) : (
                <>
                  <Video className="h-5 w-5 mr-2" />
                  {t('joinStream') || 'الانضمام للبث المباشر'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stream Dialog */}
      {stream && (
        <Dialog open={showStreamDialog} onOpenChange={(open) => {
          if (!open) {
            leaveStream();
          }
        }}>
          <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] p-0 m-0 rounded-none">
            {/* Header Overlay */}
            <div className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/80 to-transparent p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 bg-red-600 rounded-full animate-pulse" />
                  <span className="text-white font-semibold text-lg">
                    {stream.title}
                  </span>
                  <span className="text-white/80 text-sm">
                    {stream.teacher_name}
                  </span>
                </div>
                <Button
                  onClick={leaveStream}
                  variant="outline"
                  size="default"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                >
                  {t('leave') || 'مغادرة'}
                </Button>
              </div>
            </div>

            {/* Video Full Screen */}
            <div className="w-full h-full bg-black relative">
              {isConnecting && !isConnected && (
                <div className="absolute inset-0 flex items-center justify-center bg-black z-40">
                  <div className="text-center text-white">
                    <Loader2 className="h-16 w-16 animate-spin mx-auto mb-6" />
                    <p className="text-xl">{t('connectingToStream') || 'جاري الاتصال بالبث...'}</p>
                  </div>
                </div>
              )}
              
              {!isConnected && !isConnecting && (
                <div className="absolute inset-0 flex items-center justify-center bg-black z-40">
                  <div className="text-center text-white">
                    <Video className="h-16 w-16 mx-auto mb-6 text-gray-500" />
                    <p className="text-xl">{t('waitingForStream') || 'في انتظار البث...'}</p>
                  </div>
                </div>
              )}
              
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-contain"
                style={{ display: isConnected ? 'block' : 'none' }}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default LiveStreamView;
